import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { ujmsNotifications, ujmsMarkNotifRead, ujmsMarkAllNotifsRead } from "../services/ujmsApi";
import { cls, fmtDT, can } from "./ui";
import { ujmsAccess } from "../services/ujmsApi";
import { BenchWarrantButton } from "./FtaBenchWarrant";

export const NotificationBell = () => {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const [access, setAccess] = useState(null);
  const ref = useRef(null);
  const navigate = useNavigate();
  const load = useCallback(() => { ujmsNotifications().then((r) => setData(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    load();
    ujmsAccess().then((r) => setAccess(r.data)).catch(() => {});
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const click = async (n) => {
    if (!n.read) { try { await ujmsMarkNotifRead(n.id); } catch (e) { /* non-blocking */ } }
    setOpen(false); load();
    if (n.case_id) navigate(`/ujms/cases/${n.case_id}`);
  };
  const readAll = async () => { try { await ujmsMarkAllNotifsRead(); load(); } catch (e) { /* non-blocking */ } };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} data-testid="notification-bell"
        className="relative p-2 rounded-sm hover:bg-white/15 transition-colors">
        <Bell className="w-5 h-5 text-white/90" />
        {data.unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notification-badge">{data.unread > 9 ? "9+" : data.unread}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-11 w-96 bg-white border border-slate-200 rounded-sm shadow-lg z-50" data-testid="notification-panel">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200">
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Notifications</div>
            {data.unread > 0 && (
              <button className="text-xs text-blue-900 hover:underline flex items-center gap-1" onClick={readAll} data-testid="notification-read-all">
                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
            {data.items.length === 0 ? (
              <div className="text-sm text-slate-400 px-3 py-6 text-center" data-testid="notification-empty">No notifications yet.</div>
            ) : data.items.map((n) => (
              <div key={n.id} role="button" tabIndex={0} onClick={() => click(n)} onKeyDown={(e) => e.key === "Enter" && click(n)} data-testid={`notification-item-${n.id}`}
                className={cls("w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors cursor-pointer", !n.read && "bg-blue-50/60")}>
                <div className="flex items-start gap-2">
                  {!n.read && <span className="w-2 h-2 rounded-full bg-blue-700 mt-1.5 shrink-0" />}
                  <div className={cls(n.read && "pl-4")}>
                    <div className="text-sm text-slate-800">{n.body}</div>
                    <div className="text-[11px] text-slate-400 font-mono mt-0.5">{fmtDT(n.created_at)}{n.actor_name ? ` · ${n.actor_name}` : ""}</div>
                    {n.event_type === "CITATION_FTA" && n.citation_id && (
                      n.resolved
                        ? <div className="mt-1.5 text-[11px] font-semibold text-emerald-700" data-testid={`notification-fta-resolved-${n.id}`}>Bench warrant issued{n.resolved_by ? ` · ${n.resolved_by}` : ""}</div>
                        : can(access, "warrant.issue") && <div className="mt-1.5"><BenchWarrantButton citationId={n.citation_id} testId={`notification-bench-warrant-${n.id}`} onDone={load} /></div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
